import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePrivateWorkspace } from "@/api/workspaces";
import { pushComposerDraft } from "@/composer/composerDraft";
import { ROUTES } from "@/constants/routes";
import { useMainRouteParams } from "@/hooks/pane-routing";
import { emitCanvasOpenChat } from "@/pages/canvas/hooks/useCanvasWindowEvents";
import { route } from "@/utils/route";
import { FLOW_SESSION_MODES } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Bracketed slots invite editing before send — the draft is seeded, never
// auto-sent. The agent's FlowSchedule tools take it from there.
const CANVAS_SEED =
  "Create an automation on this canvas that [describe what the agent should do], running [every weekday] at [9:00am].";
const WORKSPACE_SEED =
  "Create an automation in this workspace that [describe what the agent should do], running [every weekday] at [9:00am].";

/**
 * "Create with Agent" — seeds a chat composer with an automation prompt.
 * On a canvas, the floating agent chat is nudged open and seeded in place;
 * anywhere else, a workspace chat is created (in the workspace being viewed,
 * else My Workspace) and the composer is seeded after navigation.
 *
 * Note: the agent path relies on the org having the FlowSchedule toolset
 * assigned (dark by default) — without it the agent declines and points at
 * the manual form.
 */
export function useCreateAutomationWithAgent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useMainRouteParams();
  const privateWorkspace = usePrivateWorkspace();

  const createFlowSession = useCreateFlowSession({
    onError: () => toast.error("Failed to create chat"),
  });

  const canvasId = params.canvasId;
  const routeWorkspaceId = params.workspaceId;

  const createWithAgent = useCallback(() => {
    if (canvasId) {
      // The canvas resolves (or mints) the session its floating chat opens on
      // and seeds that composer — which session it lands on isn't knowable
      // from here.
      emitCanvasOpenChat({ canvasId, composerDraft: CANVAS_SEED });
      return;
    }

    const workspaceId = routeWorkspaceId ?? privateWorkspace?.id;
    if (!workspaceId) {
      toast.error("Failed to create chat");
      return;
    }
    createFlowSession.mutate(
      {
        mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
        class: "workspace",
        workspace_id: workspaceId,
      },
      {
        onSuccess: (session) => {
          pushComposerDraft(queryClient, session.id, WORKSPACE_SEED);
          navigate(route(ROUTES.CHAT.DETAIL, { flowSessionId: session.id }));
        },
      },
    );
  }, [
    canvasId,
    createFlowSession,
    navigate,
    privateWorkspace?.id,
    queryClient,
    routeWorkspaceId,
  ]);

  return { createWithAgent, isPending: createFlowSession.isPending };
}
